import { useEffect, useRef, useState, useCallback } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChevronLeft, ChevronRight } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const CAROUSEL_ITEMS = [
  {
    id: 1,
    title: 'Texto \u0026 Código',
    description: 'Geração de textos, scripts, códigos e documentação técnica com inteligência contextual profunda.',
    image: '/assets/multimodal-text.jpg',
    color: '#00f0ff',
  },
  {
    id: 2,
    title: 'Imagem \u0026 Vídeo',
    description: 'Criação e análise de conteúdo visual. Geração de imagens, edição de vídeo e reconhecimento visual.',
    image: '/assets/multimodal-image.jpg',
    color: '#ff00c8',
  },
  {
    id: 3,
    title: 'Música \u0026 Áudio',
    description: 'Composição musical, geração de áudio, mixagem e análise de padrões sonoros em tempo real.',
    image: '/assets/multimodal-music.jpg',
    color: '#00ff9c',
  },
  {
    id: 4,
    title: 'Arquivos \u0026 Dados',
    description: 'Processamento de documentos, planilhas, bancos de dados e extração de insights estruturados.',
    image: '/assets/multimodal-files.jpg',
    color: '#7a00ff',
  },
];

function calculatePositions(itemIndex: number, activeIndex: number, _itemCount: number) {
  const angleStep = 45;
  const currentAngle = (itemIndex - activeIndex) * angleStep;
  const rotationY = currentAngle;
  const translateZ = Math.abs(currentAngle) <= 90 ? 450 : 150;
  const opacity = Math.abs(currentAngle) <= 90 ? 1 : 0.2;
  const scale = Math.abs(currentAngle) <= 45 ? 1.1 : 0.9;
  return { rotationY, translateZ, opacity, scale };
}

export default function Multimodal() {
  const sectionRef = useRef<HTMLElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const isAnimatingRef = useRef(false);

  const updateCarousel = useCallback((newIndex: number) => {
    if (isAnimatingRef.current) return;
    isAnimatingRef.current = true;

    const itemCount = CAROUSEL_ITEMS.length;
    setActiveIndex(newIndex);

    itemRefs.current.forEach((item, index) => {
      if (!item) return;
      const pos = calculatePositions(index, newIndex, itemCount);

      gsap.to(item, {
        zIndex: Math.abs(pos.rotationY) <= 45 ? 10 : 1,
        duration: 0.8,
        ease: 'power2.out',
        transform: `rotateY(${pos.rotationY}deg) translateZ(${pos.translateZ}px) scale(${pos.scale})`,
        opacity: pos.opacity,
        onComplete: () => {
          isAnimatingRef.current = false;
        },
      });
    });
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    // Initial positioning
    updateCarousel(0);

    // Entrance animation
    gsap.fromTo(
      '.multimodal-header',
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 70%',
          toggleActions: 'play none none none',
        },
      }
    );

    gsap.fromTo(
      carouselRef.current,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 55%',
          toggleActions: 'play none none none',
        },
      }
    );
  }, [updateCarousel]);

  const goNext = () => {
    const next = (activeIndex + 1) % CAROUSEL_ITEMS.length;
    updateCarousel(next);
  };

  const goPrev = () => {
    const prev = (activeIndex - 1 + CAROUSEL_ITEMS.length) % CAROUSEL_ITEMS.length;
    updateCarousel(prev);
  };

  return (
    <section
      id="multimodal"
      ref={sectionRef}
      className="relative w-full py-32 px-6 overflow-hidden"
      style={{ background: '#0a0a0e', zIndex: 5 }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="multimodal-header text-center mb-16">
          <span
            className="text-[11px] uppercase tracking-[0.25em] font-semibold mb-4 block"
            style={{ color: '#ff00c8' }}
          >
            Capacidades
          </span>
          <h2
            className="font-syncopate text-white uppercase"
            style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              textShadow: '0 0 40px rgba(255,0,200,0.15)',
            }}
          >
            Interface de Criação
          </h2>
          <p className="mt-4 text-slate-400 max-w-lg mx-auto">
            Módulos em desenvolvimento. Texto e código disponíveis hoje.
            Imagem, áudio e vídeo em integração.
          </p>
          {/* Status badge */}
          <div className="flex justify-center mt-5">
            <span
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-pill text-[10px] uppercase tracking-[0.15em] font-semibold border"
              style={{
                background: 'rgba(255,165,0,0.06)',
                borderColor: 'rgba(255,165,0,0.3)',
                color: '#ffaa00',
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#ffaa00]" />
              Em desenvolvimento
            </span>
          </div>
        </div>

        {/* 3D Carousel */}
        <div
          className="relative h-[450px] md:h-[500px]"
          style={{ perspective: '1200px' }}
        >
          {/* Navigation Arrows */}
          <button
            onClick={goPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <button
            onClick={goNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <ChevronRight className="w-5 h-5 text-white" />
          </button>

          {/* Carousel Items */}
          <div
            ref={carouselRef}
            className="absolute inset-0 flex items-center justify-center"
            style={{ transformStyle: 'preserve-3d' }}
          >
            {CAROUSEL_ITEMS.map((item, index) => {
              const pos = calculatePositions(index, activeIndex, CAROUSEL_ITEMS.length);
              const isActive = index === activeIndex;

              return (
                <div
                  key={item.id}
                  ref={(el) => { itemRefs.current[index] = el; }}
                  className="absolute w-[280px] md:w-[360px] rounded-panel overflow-hidden cursor-pointer transition-shadow duration-500"
                  style={{
                    transform: `rotateY(${pos.rotationY}deg) translateZ(${pos.translateZ}px) scale(${pos.scale})`,
                    opacity: pos.opacity,
                    zIndex: Math.abs(pos.rotationY) <= 45 ? 10 : 1,
                    boxShadow: isActive
                      ? `0 0 60px ${item.color}30, 0 20px 80px rgba(0,0,0,0.5)`
                      : '0 10px 40px rgba(0,0,0,0.3)',
                    border: `1px solid ${isActive ? item.color + '40' : 'rgba(255,255,255,0.06)'}`,
                  }}
                  onClick={() => updateCarousel(index)}
                >
                  {/* Image */}
                  <div className="relative h-[200px] md:h-[240px] overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                    />
                    <div
                      className="absolute inset-0"
                      style={{
                        background: `linear-gradient(to top, rgba(10,10,14,0.95) 0%, transparent 60%)`,
                      }}
                    />
                    {/* Title overlay */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3
                        className="font-syncopate text-sm uppercase tracking-[0.05em]"
                        style={{ color: item.color }}
                      >
                        {item.title}
                      </h3>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5" style={{ background: 'rgba(255,255,255,0.02)' }}>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      {item.description}
                    </p>
                    {isActive && (
                      <div className="mt-4 flex items-center gap-2">
                        <div
                          className="w-1.5 h-1.5 rounded-full animate-pulse"
                          style={{ background: item.color }}
                        />
                        <span
                          className="text-[10px] uppercase tracking-[0.15em]"
                          style={{ color: item.color }}
                        >
                          Ativo
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Thumbnail Navigation */}
        <div className="flex justify-center gap-3 mt-8">
          {CAROUSEL_ITEMS.map((item, index) => (
            <button
              key={item.id}
              onClick={() => updateCarousel(index)}
              className="relative w-16 h-12 rounded-lg overflow-hidden transition-all duration-300"
              style={{
                border: index === activeIndex
                  ? `2px solid ${item.color}`
                  : '2px solid transparent',
                opacity: index === activeIndex ? 1 : 0.5,
              }}
            >
              <img
                src={item.image}
                alt={item.title}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>

        {/* Upload area hint */}
        <div
          className="mt-16 p-8 rounded-panel text-center max-w-2xl mx-auto"
          style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px dashed rgba(255,255,255,0.1)',
          }}
        >
          <p className="text-slate-400 text-sm mb-4">
            Arraste imagens, vídeos, áudio ou documentos para a Vérixa processar
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {['Imagem', 'Vídeo', 'Áudio', 'Arquivo'].map((type) => (
              <span
                key={type}
                className="px-4 py-2 text-xs uppercase tracking-[0.08em] rounded-pill border border-white/10 text-slate-400"
              >
                {type}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
